import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Hourglass, CheckCircle, XCircle, ShieldCheck, ClipboardList, X } from 'lucide-react';

const DOC_LABELS = {
  aadhaar: 'Aadhaar Card', pan: 'PAN Card', voter_id: 'Voter ID',
  driving_license: 'Driving License', passport: 'Passport'
};

export default function AdminKyc() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [viewer, setViewer] = useState(null); // { title, src } for full image preview

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    api.get('/kyc/all').then(r => setSubmissions(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this KYC submission?')) return;
    setActionLoading(id + '_approve');
    try {
      const res = await api.put(`/kyc/${id}/approve`);
      toast.success(res.data.message);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally { setActionLoading(null); }
  };

  const handleReject = async (id) => {
    const note = prompt('Reason for rejection:') || 'Documents unclear or invalid. Please re-submit.';
    setActionLoading(id + '_reject');
    try {
      const res = await api.put(`/kyc/${id}/reject`, { adminNote: note });
      toast.success(res.data.message);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally { setActionLoading(null); }
  };

  const filtered = submissions.filter(k => filter === 'all' || k.status === filter);
  const counts = {
    pending: submissions.filter(k => k.status === 'pending').length,
    approved: submissions.filter(k => k.status === 'approved').length,
    rejected: submissions.filter(k => k.status === 'rejected').length,
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">KYC Verification</h1>
      <p className="page-subtitle">Review and verify user identity documents</p>

      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Pending', value: counts.pending, color: '#f59e0b', icon: Hourglass },
          { label: 'Approved', value: counts.approved, color: '#22c55e', icon: CheckCircle },
          { label: 'Rejected', value: counts.rejected, color: '#ef4444', icon: XCircle },
          { label: 'Total', value: submissions.length, color: '#3b82f6', icon: ShieldCheck },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="tab-bar" style={{ marginBottom: 20 }}>
        {[['pending', Hourglass, 'Pending'], ['approved', CheckCircle, 'Approved'], ['rejected', XCircle, 'Rejected'], ['all', ClipboardList, 'All']].map(([id, Icon, label]) => (
          <button key={id} className={`tab-btn ${filter === id ? 'active' : ''}`} onClick={() => setFilter(id)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon size={14} /> {label} ({id === 'all' ? submissions.length : counts[id] || 0})
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          filtered.length === 0 ? (
            <div className="empty-state"><div className="empty-icon"><ShieldCheck size={40} /></div><h3>No KYC submissions found</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>Date</th><th>User</th><th>Mobile</th><th>Doc Type</th><th>Doc Number</th><th>Documents</th><th>Status</th><th>Note</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map(k => (
                    <tr key={k._id}>
                      <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{new Date(k.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{k.user?.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{k.user?.email}</div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{k.user?.mobile}</td>
                      <td><span className="badge badge-info">{DOC_LABELS[k.docType] || k.docType}</span></td>
                      <td style={{ fontSize: '0.82rem' }}>{k.docNumber}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {k.docFrontImage && (
                            <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem', padding: '4px 8px' }}
                              onClick={() => setViewer({ title: `${k.user?.name} — Front`, src: k.docFrontImage })}>Front</button>
                          )}
                          {k.docBackImage && (
                            <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem', padding: '4px 8px' }}
                              onClick={() => setViewer({ title: `${k.user?.name} — Back`, src: k.docBackImage })}>Back</button>
                          )}
                          {k.selfieImage && (
                            <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: '0.72rem', padding: '4px 8px' }}
                              onClick={() => setViewer({ title: `${k.user?.name} — Selfie`, src: k.selfieImage })}>Selfie</button>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${k.status === 'approved' ? 'success' : k.status === 'pending' ? 'warning' : 'danger'}`}>
                          {k.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: 140 }}>{k.adminNote || '—'}</td>
                      <td>
                        {k.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(k._id)}
                              disabled={actionLoading === k._id + '_approve'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === k._id + '_approve' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CheckCircle size={14} /> Approve</span>)}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(k._id)}
                              disabled={actionLoading === k._id + '_reject'}
                              style={{ fontSize: '0.75rem', padding: '6px 10px' }}
                            >
                              {actionLoading === k._id + '_reject' ? '...' : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={14} /> Reject</span>)}
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                            {k.reviewedAt ? new Date(k.reviewedAt).toLocaleDateString('en-IN') : 'Processed'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>

      {/* Image viewer modal */}
      {viewer && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 16
          }}
          onClick={() => setViewer(null)}
        >
          <div className="card" style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 className="card-title">{viewer.title}</h3>
              <button onClick={() => setViewer(null)} className="btn btn-outline btn-sm" style={{ padding: 6 }}><X size={16} /></button>
            </div>
            <img src={viewer.src} alt={viewer.title} style={{ width: '100%', borderRadius: 8 }} />
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}